<script lang="ts">
  interface Props {
    csrfToken: string;
    resetToken: string;
  }

  let { csrfToken = "", resetToken = "" }: Props = $props();

  let password = $state("");
  let passwordConfirmation = $state("");
  let loading = $state(false);
  let errorMessage = $state("");
  let complete = $state(false);

  // Must match User::PASSWORD_COMPLEXITY (server-side is the real gate).
  const passwordValid = $derived(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(password)
  );
  const passwordsMatch = $derived(password === passwordConfirmation);
  const canSubmit = $derived(passwordValid && passwordsMatch && !loading);

  async function handleSubmit(e: SubmitEvent) {
    e.preventDefault();
    loading = true;
    errorMessage = "";

    try {
      const formData = new FormData();
      formData.append("user[reset_password_token]", resetToken);
      formData.append("user[password]", password);
      formData.append("user[password_confirmation]", passwordConfirmation);
      formData.append("authenticity_token", csrfToken);

      const response = await fetch("/password", {
        method: "PUT",
        credentials: "same-origin",
        body: formData,
      });

      if (response.ok || response.redirected) {
        complete = true;
      } else if (response.status === 422) {
        errorMessage =
          "Your password needs at least 8 characters, an upper- and lower-case letter, a number, and a symbol.";
      } else {
        errorMessage = "We could not reset your password. Please try again.";
      }
    } catch (error) {
      console.error("Reset password error:", error);
      errorMessage = "We could not reset your password. Please try again.";
    } finally {
      loading = false;
    }
  }
</script>

<div class="space-y-6">
  <div class="text-center">
    <h1 class="text-4xl font-bold text-foreground">Set a new password</h1>
    <p class="mt-2 text-base text-muted-foreground">Choose a strong password you can remember.</p>
  </div>

  {#if complete}
    <div class="rounded-md border border-primary/20 bg-primary/10 p-4">
      <p class="text-base text-primary">Your password has been updated.</p>
    </div>

    <p class="text-center text-base text-muted-foreground">
      <a href="/login" class="font-medium text-primary hover:text-primary/80">
        Sign in now
      </a>
    </p>
  {:else}
    {#if errorMessage}
      <div class="rounded-md border border-destructive/20 bg-destructive/10 p-4">
        <p class="text-base text-destructive">{errorMessage}</p>
      </div>
    {/if}

    <form onsubmit={handleSubmit} class="space-y-4">
      <div>
        <label for="password" class="block text-base font-medium text-foreground">New password</label>
        <input
          id="password"
          type="password"
          placeholder="••••••••"
          bind:value={password}
          required
          disabled={loading}
          class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
        />
        <p class="mt-1 text-sm text-muted-foreground">
          At least 8 characters, with an upper- and lower-case letter, a number, and a symbol
          (like ! or #).
        </p>
        {#if password && !passwordValid}
          <p class="mt-1 text-sm text-destructive">Please meet all the password rules above.</p>
        {/if}
      </div>

      <div>
        <label for="password-confirmation" class="block text-base font-medium text-foreground">
          Confirm new password
        </label>
        <input
          id="password-confirmation"
          type="password"
          placeholder="••••••••"
          bind:value={passwordConfirmation}
          required
          disabled={loading}
          class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
        />
        {#if passwordConfirmation && !passwordsMatch}
          <p class="mt-1 text-sm text-destructive">The two passwords don't match.</p>
        {/if}
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        class="w-full rounded-md bg-primary px-4 py-3 text-base font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
      >
        {loading ? "Updating..." : "Update password"}
      </button>
    </form>

    <p class="text-center text-base text-muted-foreground">
      <a href="/login" class="font-medium text-primary hover:text-primary/80">
        Back to sign in
      </a>
    </p>
  {/if}
</div>
